import {
  CalendarClock, CalendarDays, FileText, FlaskConical, Loader2, Pencil, Plus,
  Stethoscope, UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import type {
  AppointmentLinks, CabinetAppointment, RelatedNote,
} from '@/lib/cabinet/appointments';
import { statusToneClass } from '@/lib/cabinet/appointments';

export interface CabinetAppointmentCardProps {
  appointment: CabinetAppointment | null;
  statusLabels: Record<string, string>;
  typeLabels: Record<string, string>;
  links: AppointmentLinks | null;
  linksLoading: boolean;
  linksError: string;
  psyInstalled: boolean;
  periodCount: number | null;
  countsLoading: boolean;
  onCreate: () => void;
  onEdit: () => void;
  onOpenPatient: () => void;
  onOpenConclusions: () => void;
  onOpenTests: () => void;
  onOpenNote: (note: RelatedNote) => void;
  onFilterPatient: () => void;
}

function EmptyCard({
  psyInstalled, periodCount, countsLoading, onCreate,
}: {
  psyInstalled: boolean;
  periodCount: number | null;
  countsLoading: boolean;
  onCreate: () => void;
}) {
  const emptyPeriod = !countsLoading && periodCount === 0;
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="max-w-md space-y-3 text-center">
        {countsLoading ? (
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-muted-foreground" />
        ) : (
          <CalendarDays className="mx-auto h-7 w-7 text-muted-foreground" />
        )}
        <div className="text-sm font-medium">
          {countsLoading ? 'Загрузка записей…' : emptyPeriod ? 'В выбранном периоде записей нет' : 'Выберите запись'}
        </div>
        <div className="text-xs text-muted-foreground">
          {emptyPeriod
            ? 'Создайте запись на приём или выберите другой период.'
            : 'В списке слева — приёмы и сессии выбранного периода. Нажмите на запись, чтобы увидеть пациента, время, статус, заметку и связанные данные.'}
        </div>
        {!psyInstalled && (
          <div className="rounded-lg border bg-muted/30 px-3 py-2 text-left text-[11px] text-muted-foreground">
            Модуль психологии не установлен: заключения, заметки и тесты пациента
            будут недоступны. Приёмы и пациенты ядра «ГидраМедик» работают.
          </div>
        )}
        <Button size="sm" onClick={onCreate}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          Новая запись
        </Button>
      </div>
    </div>
  );
}

export function CabinetAppointmentCard({
  appointment, statusLabels, typeLabels, links, linksLoading, linksError, psyInstalled,
  periodCount, countsLoading,
  onCreate, onEdit, onOpenPatient, onOpenConclusions, onOpenTests, onOpenNote, onFilterPatient,
}: CabinetAppointmentCardProps) {
  if (!appointment) {
    return (
      <EmptyCard
        psyInstalled={psyInstalled}
        periodCount={periodCount}
        countsLoading={countsLoading}
        onCreate={onCreate}
      />
    );
  }

  const statusLabel = statusLabels[appointment.statusCode] || appointment.statusCode || 'Статус не указан';
  const typeLabel = typeLabels[appointment.typeCode] || appointment.typeCode || '';
  const notes = links?.notes || [];
  const hasOwnNote = Boolean(appointment.complaints.trim() || appointment.conclusion.trim());
  const relatedWarnings = links ? [...links.missing.map((m) => `${m}: объект не найден`), ...links.warnings] : [];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b px-3 py-2.5">
        <div className="flex items-start gap-2">
          <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">
              {appointment.number ? `Приём № ${appointment.number}` : 'Приём'}
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {appointment.appointmentAt
                ? formatValue(appointment.appointmentAt, '', 'timestamp')
                : 'Дата и время не указаны'}
            </div>
          </div>
          <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] ${statusToneClass(appointment.statusCode)}`}>
            {statusLabel}
          </span>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3">
        <section className="space-y-2 rounded-lg border bg-card p-3">
          <button
            type="button"
            className="flex w-full items-center gap-2 text-left hover:underline disabled:no-underline"
            onClick={onOpenPatient}
            disabled={!appointment.patientId}
            title={appointment.patientId ? 'Открыть карточку пациента' : 'Пациент не указан'}
          >
            <UserRound className="h-4 w-4 shrink-0 text-primary" />
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {appointment.patientName || 'Пациент не указан'}
            </span>
            {appointment.patientId && (
              <span className="shrink-0 text-[10px] text-muted-foreground">карточка</span>
            )}
          </button>
          <div className="grid grid-cols-1 gap-1.5 text-xs sm:grid-cols-2">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Stethoscope className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{appointment.doctorName || 'Врач не указан'}</span>
            </div>
            <div className="truncate text-muted-foreground">{typeLabel || 'Тип приёма не указан'}</div>
          </div>
        </section>

        <section className="space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            <FileText className="h-3.5 w-3.5" />
            Заметка
          </div>
          {appointment.complaints.trim() && (
            <div className="rounded-lg border bg-card p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Жалобы</div>
              <div className="mt-1 whitespace-pre-wrap break-words text-xs">{appointment.complaints}</div>
            </div>
          )}
          {appointment.conclusion.trim() && (
            <div className="rounded-lg border bg-card p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Заключение</div>
              <div className="mt-1 whitespace-pre-wrap break-words text-xs">{appointment.conclusion}</div>
            </div>
          )}
          {linksLoading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Загрузка заметок сессии…
            </div>
          )}
          {!linksLoading && notes.length > 0 && notes.map((note) => (
            <button
              key={note.id}
              type="button"
              className="w-full rounded-lg border bg-card p-2.5 text-left hover:bg-muted/40"
              onClick={() => onOpenNote(note)}
              title="Открыть заметку"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Заметка сессии</span>
                <span className="shrink-0 text-[10px] text-muted-foreground">
                  {note.date ? formatValue(note.date, '', 'timestamp') : ''}
                </span>
              </div>
              <div className="mt-1 whitespace-pre-wrap break-words text-xs">{note.text || 'Текст не заполнен'}</div>
            </button>
          ))}
          {!hasOwnNote && !linksLoading && notes.length === 0 && (
            <div className="rounded-lg border px-3 py-2 text-xs text-muted-foreground">
              Заметка не заполнена: нет жалоб, заключения и заметок сессии.
            </div>
          )}
        </section>

        <section className="space-y-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Связанное</div>
          {linksError && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {linksError}
            </div>
          )}
          {!linksError && !psyInstalled && (
            <div className="rounded-lg border px-3 py-2 text-xs text-muted-foreground">
              Модуль психологии не установлен — заключения и тесты пациента недоступны.
              Приёмы и карточка пациента работают.
            </div>
          )}
          {!linksError && psyInstalled && (
            <>
              {links?.denied && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                  Нет прав на часть связанных данных. Показаны только доступные разделы.
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={onFilterPatient}>
                  Все приёмы пациента
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px]"
                  onClick={onOpenConclusions}
                >
                  <FileText className="mr-1 h-3 w-3" />
                  Заключения{links ? ` (${links.conclusions.length})` : ''}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px]"
                  onClick={onOpenTests}
                >
                  <FlaskConical className="mr-1 h-3 w-3" />
                  Тесты{links ? ` (${links.assignments.length})` : ''}
                </Button>
              </div>
              {relatedWarnings.length > 0 && (
                <div className="space-y-0.5 text-[10px] text-muted-foreground">
                  {relatedWarnings.map((w) => <div key={w}>{w}</div>)}
                </div>
              )}
              {links && links.conclusions.length > 0 && (
                <div className="rounded-lg border bg-card p-2.5">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Последнее заключение</div>
                  <div className="mt-1 whitespace-pre-wrap break-words text-xs">
                    {links.conclusions[0].text || 'Текст не заполнен'}
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    {links.conclusions[0].date ? formatValue(links.conclusions[0].date, '', 'date') : 'Дата не указана'}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      <div className="shrink-0 border-t bg-card/40 px-3 py-2">
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" className="h-7 px-2 text-[11px]" onClick={onEdit}>
            <Pencil className="mr-1 h-3 w-3" />
            Открыть
          </Button>
          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={onCreate}>
            <Plus className="mr-1 h-3 w-3" />
            Новая
          </Button>
          <Button size="sm" variant="outline" className="ml-auto h-7 px-2 text-[11px]" onClick={onOpenPatient} disabled={!appointment.patientId}>
            <UserRound className="mr-1 h-3 w-3" />
            Пациент
          </Button>
        </div>
      </div>
    </div>
  );
}
